import React, { useState } from 'react';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Box,
    Typography,
    Paper,
    Button
} from '@mui/material';
import { useCreateAwsConnection, useUpdateAwsConnection, useTestAwsConnection, useDeleteAwsConnection, useAwsConnectionCloudFormation, useAwsConnectionTerraform } from '../hooks/useAwsConnections';
import { useTenants } from '../hooks/useTenants';
import { AWS_REGIONS } from '../constants/awsRegions';

interface DetailsEditorActions {
    onSave?: () => Promise<void> | void;
    onDelete?: () => Promise<void> | void;
    onCancel?: () => void;
}

interface DetailsEditorState {
    saveEnabled?: boolean;
    deleteEnabled?: boolean;
    saving?: boolean;
    deleting?: boolean;
}

interface AwsConnectionEditProps {
    connection?: any;
    editorActions?: DetailsEditorActions;
    setEditorState?: (state: Partial<DetailsEditorState>) => void;
    onSave?: (data: any) => Promise<void>;
}



export const AwsConnectionEdit: React.FC<AwsConnectionEditProps> = ({
    connection,
    editorActions = {},
    setEditorState = () => {},
    onSave
}) => {
    const { data: tenants = [] } = useTenants();
    const [formData, setFormData] = useState({
        name: connection?.name || '',
        description: connection?.description || '',
        accountId: connection?.accountId || '',
        region: connection?.region || 'us-east-1',
        tenantId: connection?.tenantId || tenants[0]?.id || '',
        roleArn: connection?.roleArn || ''
    });
    const [cloudFormationTemplate, setCloudFormationTemplate] = useState('');
    const [createdConnection, setCreatedConnection] = useState<any>(null);
    const [step, setStep] = useState<'form' | 'template' | 'complete'>(connection ? 'form' : 'form');

    const createConnection = useCreateAwsConnection();
    const updateConnection = useUpdateAwsConnection();
    const testConnection = useTestAwsConnection();
    const deleteConnection = useDeleteAwsConnection();
    const getCloudFormation = useAwsConnectionCloudFormation();
    const getTerraform = useAwsConnectionTerraform();

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isFormValid && editorActions.onSave) {
            e.preventDefault();
            editorActions.onSave();
        } else if (e.key === 'Escape' && editorActions.onCancel) {
            e.preventDefault();
            editorActions.onCancel();
        }
    };

    const isFormValid = formData.name.trim() !== '' && 
        (!connection ? (formData.accountId.trim() !== '' && formData.tenantId.trim() !== '') : true);

    // Update form data when connection changes
    React.useEffect(() => {
        if (connection) {
            setFormData({
                name: connection.name || '',
                description: connection.description || '',
                accountId: connection.accountId || '',
                region: connection.region || 'us-east-1',
                tenantId: connection.tenantId || tenants[0]?.id || '',
                roleArn: connection.roleArn || ''
            });
            setStep('form');
        } else {
            setFormData({
                name: '',
                description: '',
                accountId: '',
                region: 'us-east-1',
                tenantId: tenants[0]?.id || '',
                roleArn: ''
            });
            setStep('form');
            setCloudFormationTemplate('');
            setCreatedConnection(null);
        }
    }, [connection, tenants]);

    // Update editor state based on form validation
    React.useEffect(() => {
        setEditorState({ 
            saveEnabled: isFormValid,
            deleteEnabled: !!connection
        });
    }, [isFormValid, connection, setEditorState]);

    // Register save action with parent's business logic
    React.useEffect(() => {
        if (onSave) {
            editorActions.onSave = async () => {
                setEditorState({ saving: true });
                try {
                    await onSave(formData);
                } finally {
                    setEditorState({ saving: false });
                }
            };
        } else {
            editorActions.onSave = async () => {
                setEditorState({ saving: true });
                try {
                    if (connection) {
                        // Update existing connection with role ARN
                        await updateConnection.mutateAsync({
                            connectionId: connection.id,
                            tenantId: connection.tenantId,
                            roleArn: formData.roleArn
                        });
                    } else {
                        // Create new connection
                        const result = await createConnection.mutateAsync({
                            tenantId: formData.tenantId,
                            name: formData.name,
                            description: formData.description,
                            accountId: formData.accountId,
                            region: formData.region,
                            accessMethod: 'cross-account-role'
                        });
                        
                        setCreatedConnection(result.connection);
                        setCloudFormationTemplate(result.cloudFormationTemplate);
                        setStep('template');
                    }
                } finally {
                    setEditorState({ saving: false });
                }
            };
        }
    }, [editorActions, onSave, formData, setEditorState, connection, updateConnection, createConnection]);

    // Register delete action
    React.useEffect(() => {
        if (connection) {
            editorActions.onDelete = async () => {
                setEditorState({ deleting: true });
                try {
                    await deleteConnection.mutateAsync({
                        connectionId: connection.id
                    });
                    // Navigate back to connections list after successful deletion
                    window.location.href = '/connections/aws';
                } finally {
                    setEditorState({ deleting: false });
                }
            };
        }
    }, [editorActions, connection, deleteConnection, setEditorState]);

    // Register cancel action
    React.useEffect(() => {
        editorActions.onCancel = () => {
            // Cancel action will be handled by parent
        };
    }, [editorActions]);

    const downloadTemplate = () => {
        const blob = new Blob([cloudFormationTemplate], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `servicecatalog-role-${formData.name}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleComplete = async () => {
        if (createdConnection && formData.roleArn) {
            try {
                await updateConnection.mutateAsync({
                    connectionId: createdConnection.id,
                    tenantId: createdConnection.tenantId,
                    roleArn: formData.roleArn
                });
                setStep('complete');
                if (onSave) {
                    onSave(formData);
                }
            } catch (error) {
                console.error('Failed to update connection with role ARN:', error);
            }
        }
    };

    if (step === 'template') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="info">
                    Deploy this CloudFormation template in your AWS account to create the required IAM role.
                </Alert>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="h6" gutterBottom>
                        CloudFormation Template
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button variant="outlined" onClick={downloadTemplate}>
                            Download Template
                        </Button>
                    </Box>
                    <TextField
                        multiline
                        rows={10}
                        value={cloudFormationTemplate}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    />
                </Paper>
                <Alert severity="warning">
                    After deploying the template, copy the Role ARN from the CloudFormation outputs and paste it below.
                </Alert>
                <TextField
                    label="Role ARN"
                    value={formData.roleArn}
                    onChange={(e) => handleInputChange('roleArn', e.target.value)}
                    placeholder="arn:aws:iam::123456789012:role/ServiceCatalog-..."
                    fullWidth
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={async () => {
                            if (createdConnection && formData.roleArn) {
                                try {
                                    await updateConnection.mutateAsync({
                                        connectionId: createdConnection.id,
                                        tenantId: createdConnection.tenantId,
                                        roleArn: formData.roleArn
                                    });
                                    await testConnection.mutateAsync({
                                        connectionId: createdConnection.id
                                    });
                                } catch (error) {
                                    console.error('Test failed:', error);
                                }
                            }
                        }}
                        disabled={!formData.roleArn || testConnection.isPending}
                    >
                        Test Connection
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleComplete}
                        disabled={!formData.roleArn}
                    >
                        Complete Setup
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onKeyDown={handleKeyDown} tabIndex={0}>
            {!connection && (
                <FormControl fullWidth required>
                    <InputLabel>Tenant</InputLabel>
                    <Select
                        value={formData.tenantId}
                        onChange={(e) => handleInputChange('tenantId', e.target.value)}
                        label="Tenant"
                    >
                        {tenants.map(tenant => (
                            <MenuItem key={tenant.id} value={tenant.id}>
                                {tenant.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            
            <TextField
                label="Connection Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                fullWidth
                InputProps={{ readOnly: !!connection }}
            />
            
            <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={2}
                fullWidth
                InputProps={{ readOnly: !!connection }}
            />
            
            {!connection && (
                <>
                    <TextField
                        label="AWS Account ID"
                        value={formData.accountId}
                        onChange={(e) => handleInputChange('accountId', e.target.value)}
                        required
                        fullWidth
                        placeholder="123456789012"
                        inputProps={{ pattern: '[0-9]{12}' }}
                    />
                    
                    <FormControl fullWidth required>
                        <InputLabel>AWS Region</InputLabel>
                        <Select
                            value={formData.region}
                            onChange={(e) => handleInputChange('region', e.target.value)}
                            label="AWS Region"
                        >
                            {AWS_REGIONS.map(region => (
                                <MenuItem key={region} value={region}>
                                    {region}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )}

            {connection && (
                <>
                    <TextField
                        label="AWS Account ID"
                        value={formData.accountId}
                        InputProps={{ readOnly: true }}
                        fullWidth
                    />
                    
                    <TextField
                        label="AWS Region"
                        value={formData.region}
                        InputProps={{ readOnly: true }}
                        fullWidth
                    />
                    
                    {!connection.roleArn && (
                        <Alert severity="info">
                            This connection is inactive. To activate it, deploy the CloudFormation or Terraform template in your AWS account and enter the Role ARN below.
                        </Alert>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                try {
                                    const template = await getCloudFormation.mutateAsync({ connectionId: connection.id });
                                    const blob = new Blob([template], { type: 'application/json' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `servicecatalog-role-${connection.name}.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                } catch (error) {
                                    console.error('Failed to download CloudFormation template:', error);
                                }
                            }}
                            disabled={getCloudFormation.isPending}
                        >
                            Download CloudFormation
                        </Button>
                        
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                try {
                                    const template = await getTerraform.mutateAsync({ connectionId: connection.id });
                                    const blob = new Blob([template], { type: 'text/plain' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `servicecatalog-role-${connection.name}.tf`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                } catch (error) {
                                    console.error('Failed to download Terraform template:', error);
                                }
                            }}
                            disabled={getTerraform.isPending}
                        >
                            Download Terraform
                        </Button>
                    </Box>
                    
                    <TextField
                        label="Role ARN"
                        value={formData.roleArn}
                        onChange={(e) => handleInputChange('roleArn', e.target.value)}
                        placeholder="arn:aws:iam::123456789012:role/ServiceCatalog-..."
                        fullWidth
                        helperText={!connection.roleArn ? "Enter the Role ARN from your CloudFormation stack outputs" : ""}
                    />
                    
                    {connection.roleArn && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={async () => {
                                    try {
                                        await testConnection.mutateAsync({ connectionId: connection.id });
                                    } catch (error) {
                                        console.error('Test failed:', error);
                                    }
                                }}
                                disabled={testConnection.isPending}
                            >
                                {testConnection.isPending ? 'Testing...' : 'Test Connection'}
                            </Button>
                            
                            <Box sx={{ 
                                px: 1, 
                                py: 0.5, 
                                borderRadius: 1, 
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                bgcolor: connection.status === 'active' ? 'success.light' : 
                                        connection.status === 'error' ? 'error.light' : 'grey.300',
                                color: connection.status === 'active' ? 'success.dark' : 
                                       connection.status === 'error' ? 'error.dark' : 'grey.700'
                            }}>
                                {connection.status === 'active' ? 'Active' : 
                                 connection.status === 'error' ? 'Error' : 'Inactive'}
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {testConnection.isSuccess && (
                <Alert severity="success">
                    ✅ Connection test successful! The role is properly configured.
                    <br />Account ID: {testConnection.data?.details?.accountId}
                    <br />Role ARN: {testConnection.data?.details?.roleArn}
                </Alert>
            )}
        </Box>
    );
};